/**
 * api/queueEndpoints.ts
 * SINGLE SOURCE OF TRUTH for SK Queue API URLs. Reconciled 1:1 against the live
 * sk-queue-api route table (src/routes/*.ts on branch dev): all product routes
 * mount under /v1 behind platform-token auth; webhooks + health are unversioned.
 */

import { QUEUE_V1 } from './config';

// ── Queues (config / operate) ───────────────────────────────────────────────
export const Queue_List_Url     = `${QUEUE_V1}/queues`;                              // GET list · POST create (Tier-1)
export const Queue_Url          = (id: string) => `${QUEUE_V1}/queues/${id}`;        // GET one · PATCH (T2 operator / T1 config)
export const Queue_Allocate_Url = (id: string) => `${QUEUE_V1}/queues/${id}/allocate`; // POST manual "call next"
export const Queue_Sweep_Url    = (id: string) => `${QUEUE_V1}/queues/${id}/sweep`;    // POST run no-show/expiry sweeps
export const Queue_Resources_Url = (id: string) => `${QUEUE_V1}/queues/${id}/resources`;           // POST create table
export const Queue_Resource_Url  = (id: string, ref: string) => `${QUEUE_V1}/queues/${id}/resources/${ref}`; // DELETE table

// ── Entries (intake / lifecycle) ─────────────────────────────────────────────
export const Queue_Entries_Url  = (queueId: string) => `${QUEUE_V1}/queues/${queueId}/entries`; // POST join (idempotent)
export const Entry_Url          = (id: string) => `${QUEUE_V1}/entries/${id}`;          // GET detail + ledger
export const Entry_Verify_Url   = (id: string) => `${QUEUE_V1}/entries/${id}/verify`;   // POST OTP
export const Entry_Acknowledge_Url = (id: string) => `${QUEUE_V1}/entries/${id}/acknowledge`; // POST "on my way" (rev 5)
export const Entry_Serve_Url    = (id: string) => `${QUEUE_V1}/entries/${id}/serve`;    // POST serve (rev 5; was /seat)
export const Entry_Done_Url     = (id: string) => `${QUEUE_V1}/entries/${id}/done`;     // POST done (rev 5; was /release)
export const Entry_Seat_Url     = (id: string) => `${QUEUE_V1}/entries/${id}/seat`;     // POST seat (back-compat alias of /serve)
export const Entry_Release_Url  = (id: string) => `${QUEUE_V1}/entries/${id}/release`;  // POST settle (back-compat alias of /done)
export const Entry_Remove_Url   = (id: string) => `${QUEUE_V1}/entries/${id}/remove`;   // POST remove (staff/guest)

// ── Read models (engine surfaces + boards / monitors) ────────────────────────
export const Dashboard_Url          = `${QUEUE_V1}/dashboard`;                          // GET engine home rollup (rev 5)
export const Queue_Overview_Url     = (id: string) => `${QUEUE_V1}/queues/${id}/overview`; // GET Queue Detail → Overview (rev 5)
export const Queue_Live_Url         = (id: string) => `${QUEUE_V1}/queues/${id}/live`;
export const Queue_Activity_Url     = (id: string) => `${QUEUE_V1}/queues/${id}/activity`;
export const Queue_Floor_Url        = (id: string) => `${QUEUE_V1}/queues/${id}/floor`;
export const Queue_NowServing_Url   = (id: string) => `${QUEUE_V1}/queues/${id}/now-serving`;
export const Queue_VoiceMonitor_Url = (id: string) => `${QUEUE_V1}/queues/${id}/voice-monitor`;
export const Queue_ChatMonitor_Url  = (id: string) => `${QUEUE_V1}/queues/${id}/chat-monitor`;

// ── Entitlements ─────────────────────────────────────────────────────────────
export const Entitlements_Url = `${QUEUE_V1}/entitlements`;

// ── Platform product catalog (request-a-queue dropdowns, rev 5) ──────────────
export const Catalog_Products_Url = `${QUEUE_V1}/catalog/products`;

// ── Pool units / resources (POOL allocation targets, dual-state) ─────────────
export const Queue_PoolUnits_Url = (queueId: string) => `${QUEUE_V1}/queues/${queueId}/pool-units`; // GET list · POST create
export const PoolUnit_Url        = (id: string) => `${QUEUE_V1}/pool-units/${id}`;       // PATCH dual-state / capacity (rev 5)
